import { Link } from "react-router-dom";
import HeroSection from "./components/HeroSection";
import CategorySection from "./components/CategorySection";
import StatsSection from "./components/StatsSection";
import DramaSection from "./components/DramaSection";
import AnimeSection from "./components/AnimeSection";
import TestimonialSection from "./components/TestimonialSection";
import NewsletterCTA from "./components/NewsletterCTA";
import MovieCard from "@/components/base/MovieCard";
import { getHotMovies, getNewMovies, movieCategories } from "@/mocks/movies";

export default function Home() {
  const hotMovies = getHotMovies(8);
  const newMovies = getNewMovies(8);

  const schemaData = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Organization",
        "@id": `${window.location.origin}/#organization`,
        name: "蓝莓影院",
        url: window.location.origin,
        description: "蓝莓影院提供高清免费在线观影体验，海量免费电视剧全集、最新电影大全，不缓冲流畅播放，每日更新热门影视资源。覆盖电影、电视剧、动漫、综艺等全品类影视内容，无需付费即点即看。",
        sameAs: ["https://weibo.com", "https://bilibili.com"],
        contactPoint: {
          "@type": "ContactPoint",
          telephone: "400-888-0000",
          contactType: "customer service",
          availableLanguage: "Chinese",
        },
      },
      {
        "@type": "WebSite",
        "@id": `${window.location.origin}/#website`,
        name: "蓝莓影院",
        url: window.location.origin,
        description: "蓝莓影院 - 高清免费在线观看平台",
        potentialAction: {
          "@type": "SearchAction",
          "target": `${window.location.origin}/search?q={search_term_string}`,
          "query-input": "required name=search_term_string",
        },
      },
      {
        "@type": "WebPage",
        "@id": `${window.location.origin}/#webpage`,
        name: "蓝莓影院 - 高清免费在线观看 免费电视剧全集 最新高清电影大全",
        description: "蓝莓影院提供高清免费在线观影体验，海量免费电视剧全集、最新电影大全，不缓冲流畅播放，每日更新热门影视资源，无需付费即点即看。涵盖电影、电视剧、动漫、综艺等全品类影视内容。",
        url: window.location.origin,
        isPartOf: { "@id": `${window.location.origin}/#website` },
        about: { "@id": `${window.location.origin}/#organization` },
      },
    ],
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaData) }}
      />

      <HeroSection />
      <CategorySection />
      <StatsSection />

      {/* 热门推荐 */}
      <section className="max-w-[1400px] mx-auto px-4 md:px-6 pt-10 md:pt-14 pb-4">
        <div className="flex items-center justify-between mb-5 md:mb-6">
          <div>
            <h2 className="text-xl md:text-2xl font-bold text-foreground-950 font-heading">
              热门推荐
            </h2>
            <p className="text-sm text-foreground-500 mt-1">大家都在看的精彩内容</p>
          </div>
          <Link
            to="/category/dianying"
            className="text-sm text-primary-500 hover:text-primary-600 font-medium whitespace-nowrap cursor-pointer flex items-center gap-1"
          >
            查看更多 <i className="ri-arrow-right-line"></i>
          </Link>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-4 gap-4 md:gap-5" data-product-shop>
          {hotMovies.map((movie) => (
            <MovieCard
              key={movie.id}
              id={movie.id}
              title={movie.title}
              image={movie.image}
              rating={movie.rating}
              category={movie.category}
              year={movie.year}
            />
          ))}
        </div>
      </section>

      <DramaSection />

      {/* 最新更新 */}
      <section className="bg-background-100 py-10 md:py-14">
        <div className="max-w-[1400px] mx-auto px-4 md:px-6">
          <div className="flex items-center justify-between mb-5 md:mb-6">
            <div>
              <h2 className="text-xl md:text-2xl font-bold text-foreground-950 font-heading">
                最新更新
              </h2>
              <p className="text-sm text-foreground-500 mt-1">每日更新，精彩不容错过</p>
            </div>
            <Link
              to="/category/dianying"
              className="text-sm text-primary-500 hover:text-primary-600 font-medium whitespace-nowrap cursor-pointer flex items-center gap-1"
            >
              查看更多 <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-4 gap-4 md:gap-5" data-product-shop>
            {newMovies.map((movie) => (
              <MovieCard
                key={movie.id}
                id={movie.id}
                title={movie.title}
                image={movie.image}
                rating={movie.rating}
                category={movie.category}
                year={movie.year}
                isNew
              />
            ))}
          </div>
        </div>
      </section>

      <AnimeSection />

      {/* 特色专题 */}
      <section className="max-w-[1400px] mx-auto px-4 md:px-6 py-10 md:py-14">
        <div className="mb-5 md:mb-6">
          <h2 className="text-xl md:text-2xl font-bold text-foreground-950 font-heading">
            特色专题
          </h2>
          <p className="text-sm text-foreground-500 mt-1">精选分类，快速找到你想看的</p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 gap-4 md:gap-5">
          {[
            {
              title: "科幻巨制",
              desc: "探索未知宇宙的无限可能",
              slug: "dianying",
              image: "https://readdy.ai/api/search-image?query=Cinematic%20sci-fi%20movie%20collage%20with%20futuristic%20spaceships%20orbiting%20distant%20planets%20neon%20light%20trails%20cosmic%20nebula%20background%20deep%20space%20aesthetic%20warm%20amber%20and%20teal%20accents%20cinematic%20atmosphere&width=800&height=400&seq=feature-sci-fi&orientation=landscape",
            },
            {
              title: "热血动漫",
              desc: "燃爆你的每一个细胞",
              slug: "dongman",
              image: "https://readdy.ai/api/search-image?query=Dynamic%20anime%20style%20illustration%20with%20epic%20battle%20scene%20warriors%20with%20glowing%20energy%20auras%20dramatic%20action%20poses%20vibrant%20colorful%20explosions%20motion%20lines%20cinematic%20anime%20composition%20warm%20fiery%20tones&width=800&height=400&seq=feature-anime&orientation=landscape",
            },
          ].map((item) => (
            <Link
              key={item.title}
              to={`/category/${item.slug}`}
              className="group relative h-48 md:h-56 rounded-xl overflow-hidden cursor-pointer"
            >
              <img
                src={item.image}
                alt={item.title}
                title={`${item.title} - 蓝莓影院高清免费在线观看`}
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent"></div>
              <div className="absolute bottom-0 left-0 right-0 p-5">
                <h3 className="text-white font-bold text-lg font-heading">{item.title}</h3>
                <p className="text-white/70 text-sm mt-1">{item.desc}</p>
              </div>
            </Link>
          ))}
        </div>
      </section>

      <TestimonialSection />
      <NewsletterCTA />

      {/* 底部快速导航分类 */}
      <section className="max-w-[1400px] mx-auto px-4 md:px-6 py-10 md:py-14">
        <div className="mb-5 md:mb-6">
          <h2 className="text-xl md:text-2xl font-bold text-foreground-950 font-heading">
            全部分类
          </h2>
          <p className="text-sm text-foreground-500 mt-1">点击进入各大分类，发现更多精彩内容</p>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          {movieCategories.map((cat, idx) => (
            <Link
              key={cat.slug}
              to={`/category/${cat.slug}`}
              className="flex items-center gap-3 p-4 rounded-xl bg-background-100 hover:bg-background-200 transition-colors cursor-pointer"
            >
              <span className="w-10 h-10 rounded-lg bg-primary-100 flex items-center justify-center">
                <i className={`${cat.icon} text-primary-500 text-lg`}></i>
              </span>
              <div>
                <p className="text-sm font-semibold text-foreground-950">{cat.name}</p>
                <p className="text-xs text-foreground-500">海量资源 · 每日更新</p>
              </div>
            </Link>
          ))}
        </div>
      </section>
    </>
  );
}